import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFirebase } from '../contexts/FirebaseContext';
import { ArrowLeft, FileText, HelpCircle, CheckCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const ViewOnlineTestQuestions = () => {
  const navigate = useNavigate();
  const { testId } = useParams();
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const testNameParam = urlParams.get('testName');
  const fromParam = urlParams.get('from');
  
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const { getOnlineTests, getOnlineTestQuestions } = useFirebase();

  useEffect(() => {
    loadData();
  }, [testId]);

  // Safely format Firestore Timestamp or Date or ISO string to locale string
  const formatDateTime = (value) => {
    if (!value) return 'N/A'
    try {
      let d
      if (value instanceof Date) d = value
      else if (value.toDate && typeof value.toDate === 'function') d = value.toDate()
      else d = new Date(value)
      if (isNaN(d.getTime())) return 'N/A'
      return d.toLocaleString()
    } catch {
      return 'N/A'
    }
  }

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load test data
      const testsData = await getOnlineTests();
      const currentTest = testsData.find(t => t.id === testId);
      
      if (!currentTest) {
        toast.error('Online test not found');
        navigate('/home?tab=online');
        return;
      }
      
      setTest(currentTest);
      
      // Load existing questions for this online test
      const qs = await getOnlineTestQuestions(testId)
      setQuestions(qs || [])
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load test questions');
    } finally {
      setLoading(false);
    }
  };

  // Helper to choose the first non-empty string from candidates
  const pick = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && String(v).trim() !== '') return v
    }
    return ''
  }

  const [questions, setQuestions] = useState([])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Test not found</h3>
        <button onClick={() => navigate('/home?tab=online')} className="btn-primary">
          Back to Tests
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header (simplified) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/home?tab=online')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Test Details</h1>
            <div className="text-sm text-gray-600">For: {test.testName}</div>
          </div>
        </div>
      </div>

      {/* Test Information */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-blue-900">Test Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {test.testName}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {test.duration || 60} minutes
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Questions</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {test.totalQuestions || test.questionCount || 0}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Marks</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {test.totalMarks || 0}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {formatDateTime(test.startDate)}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date & Time</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {formatDateTime(test.endDate)}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {formatDateTime(test.createdAt)}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Updated At</label>
            <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900">
              {formatDateTime(test.updatedAt)}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <HelpCircle className="h-4 w-4 text-gray-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Questions ({questions.length})</h2>
        </div>

        {questions.length === 0 ? (
          <div className="text-gray-600">No questions added yet.</div>
        ) : (
          <div className="space-y-4">
            {questions.map((q, idx) => {
              // Resolve option texts from multiple possible shapes
              const arrayOpts = Array.isArray(q.options) ? q.options : (Array.isArray(q.optionTexts) ? q.optionTexts : null)
              const optionTexts = {
                optionA: pick(q.optionTexts && q.optionTexts.optionA, arrayOpts && arrayOpts[0], q.optionA, q.option1, q.optionAText),
                optionB: pick(q.optionTexts && q.optionTexts.optionB, arrayOpts && arrayOpts[1], q.optionB, q.option2, q.optionBText),
                optionC: pick(q.optionTexts && q.optionTexts.optionC, arrayOpts && arrayOpts[2], q.optionC, q.option3, q.optionCText),
                optionD: pick(q.optionTexts && q.optionTexts.optionD, arrayOpts && arrayOpts[3], q.optionD, q.option4, q.optionDText)
              }

              // Determine correct option key from several shapes
              let correctKey = null
              if (q.options && typeof q.options === 'object' && !Array.isArray(q.options)) {
                correctKey = ['optionA','optionB','optionC','optionD'].find(k => q.options[k]) || null
              }
              if (!correctKey && typeof q.correctAnswer === 'number') {
                correctKey = ['optionA','optionB','optionC','optionD'][Math.max(0, Math.min(3, q.correctAnswer))]
              }
              if (!correctKey && typeof q.correctAnswer === 'string') {
                const upper = q.correctAnswer.trim().toUpperCase()
                if (['A','B','C','D'].includes(upper)) correctKey = `option${upper}`
              }
              if (!correctKey && typeof q.answer === 'string') {
                const upper = q.answer.trim().toUpperCase()
                if (['A','B','C','D'].includes(upper)) correctKey = `option${upper}`
              }
              const correctText = correctKey ? (optionTexts[correctKey] || '') : ''
              return (
                <div key={q.id || idx} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-2">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-purple-100 text-purple-700 text-sm font-semibold">Q{idx + 1}</span>
                      <div className="font-medium text-gray-900 mt-1">{q.question}</div>
                    </div>
                    <div className="text-sm text-gray-500">Marks: {q.marks || 0}</div>
                  </div>
                  
                  {/* Question Image Display */}
                  {(q.questionImageURL || q.questionImage) ? (
                    <div className="mt-4 mb-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Question Image:
                        </div>
                        <img 
                          src={q.questionImageURL || q.questionImage} 
                          alt={`Question ${idx + 1} image`} 
                          className="max-w-full max-h-96 object-contain rounded border border-gray-300 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => window.open(q.questionImageURL || q.questionImage, '_blank')}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div className="text-red-500 text-sm mt-2 hidden">
                          ⚠️ Failed to load image. The image may have been deleted or the URL is invalid.
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className={`px-3 py-2 rounded border ${correctKey==='optionA' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200'}`}>
                      <span className="mr-1 font-medium">A.</span>
                      <span>{optionTexts.optionA || ''}</span>
                      {correctKey==='optionA' && <span className="ml-2 text-green-700">✓ Correct</span>}
                    </div>
                    <div className={`px-3 py-2 rounded border ${correctKey==='optionB' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200'}`}>
                      <span className="mr-1 font-medium">B.</span>
                      <span>{optionTexts.optionB || ''}</span>
                      {correctKey==='optionB' && <span className="ml-2 text-green-700">✓ Correct</span>}
                    </div>
                    <div className={`px-3 py-2 rounded border ${correctKey==='optionC' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200'}`}>
                      <span className="mr-1 font-medium">C.</span>
                      <span>{optionTexts.optionC || ''}</span>
                      {correctKey==='optionC' && <span className="ml-2 text-green-700">✓ Correct</span>}
                    </div>
                    <div className={`px-3 py-2 rounded border ${correctKey==='optionD' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200'}`}>
                      <span className="mr-1 font-medium">D.</span>
                      <span>{optionTexts.optionD || ''}</span>
                      {correctKey==='optionD' && <span className="ml-2 text-green-700">✓ Correct</span>}
                    </div>
                  </div>
                  {q.explanation ? (
                    <div className="mt-3 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded px-3 py-2">
                      <span className="font-semibold">Explanation:</span> {q.explanation}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOnlineTestQuestions;
